import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { baseStyles } from '../styles/shared.js';
import './sysml-editor-group.js';
import './sysml-resize-handle.js';

// ---------------------------------------------------------------------------
// <sysml-editor-zone> — Multi-group editor layout manager
//
// Manages 1-4 editor groups in a horizontal split layout. Each group renders
// its content via a named slot. The default layout has 2 groups: an editor
// group (left) and a diagram group (right), matching VS Code's split editor
// pattern.
//
// Split operations add groups; close operations remove them. A resize handle
// appears between each pair of adjacent groups.
//
// The workspace provides content for each group via named slots:
//   <sysml-editor-zone>
//     <div slot="group-0"> ... editor ... </div>
//     <div slot="group-1"> ... diagram ... </div>
//   </sysml-editor-zone>
// ---------------------------------------------------------------------------

/** Describes one group in the split layout */
export interface EditorGroupState {
  id: string;
  /** Percentage width of this group within the zone */
  sizePct: number;
}

@customElement('sysml-editor-zone')
export class SysmlEditorZone extends LitElement {
  static override styles = [
    baseStyles,
    css`
      :host {
        display: flex;
        flex-direction: row;
        flex: 1;
        min-width: 0;
        min-height: 0;
        overflow: hidden;
      }

      .group-wrapper {
        min-width: 0;
        overflow: hidden;
        display: flex;
        flex-direction: column;
      }

      /* Slotted group content fills the wrapper */
      ::slotted(*) {
        display: flex;
        flex-direction: column;
        flex: 1;
        min-height: 0;
        min-width: 0;
      }
    `,
  ];

  /** Ordered list of group states */
  @property({ type: Array }) groups: EditorGroupState[] = [
    { id: 'editor', sizePct: 50 },
    { id: 'diagram', sizePct: 50 },
  ];

  /** ID of the focused group (receives keyboard shortcuts) */
  @property({ attribute: 'focused-group' }) focusedGroup = 'editor';

  /** Focus mode — 'none' shows all groups, 'editor'/'diagram' shows only that group */
  @property({ attribute: 'focus-mode' }) focusMode: 'none' | string = 'none';

  @state() private _resizing = false;

  override render() {
    const visibleGroups = this.focusMode === 'none'
      ? this.groups
      : this.groups.filter((g) => g.id === this.focusMode);

    const parts: unknown[] = [];

    visibleGroups.forEach((group, idx) => {
      // Group wrapper with size
      const width = visibleGroups.length === 1
        ? '100%'
        : `${group.sizePct}%`;

      parts.push(html`
        <div
          class="group-wrapper"
          style="width: ${width}; ${this._resizing ? 'transition: none;' : ''}"
          @click=${() => this._onGroupFocus(group.id)}
        >
          <slot name=${`group-${group.id}`}></slot>
        </div>
      `);

      // Add resize handle between groups (not after last)
      if (idx < visibleGroups.length - 1) {
        parts.push(html`
          <sysml-resize-handle
            .container=${this as HTMLElement}
            @resize=${(e: CustomEvent<{ percentage: number }>) =>
              this._onGroupResize(idx, e.detail.percentage)}
            @resize-start=${() => { this._resizing = true; }}
            @resize-end=${() => { this._resizing = false; }}
            @collapse=${() => this._onToggleFocusMode()}
          ></sysml-resize-handle>
        `);
      }
    });

    return parts;
  }

  // -- Event handlers --------------------------------------------------------

  private _onGroupFocus(groupId: string): void {
    if (this.focusedGroup !== groupId) {
      this.dispatchEvent(
        new CustomEvent('group-focus', {
          detail: { groupId },
          bubbles: true,
          composed: true,
        }),
      );
    }
  }

  private _onToggleFocusMode(): void {
    // Double-click on center handle → toggle between split and editor-only
    const newMode = this.focusMode === 'none' ? 'editor' : 'none';
    this.dispatchEvent(
      new CustomEvent('focus-mode-change', {
        detail: { mode: newMode },
        bubbles: true,
        composed: true,
      }),
    );
  }

  private _onGroupResize(leftIdx: number, percentage: number): void {
    // percentage is the left group's percentage
    const groups = [...this.groups];
    if (leftIdx >= groups.length - 1) return;

    const totalPct = groups[leftIdx].sizePct + groups[leftIdx + 1].sizePct;
    const leftPct = Math.max(15, Math.min(totalPct - 15, percentage));
    const rightPct = totalPct - leftPct;

    groups[leftIdx] = { ...groups[leftIdx], sizePct: leftPct };
    groups[leftIdx + 1] = { ...groups[leftIdx + 1], sizePct: rightPct };

    this.dispatchEvent(
      new CustomEvent('groups-resize', {
        detail: { groups },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /** Split a group by adding a new group after it */
  splitGroup(groupId: string, newGroupId: string): EditorGroupState[] {
    const idx = this.groups.findIndex((g) => g.id === groupId);
    if (idx < 0 || this.groups.length >= 4) return this.groups;

    const current = this.groups[idx];
    const halfSize = current.sizePct / 2;
    const newGroups = [...this.groups];
    newGroups[idx] = { ...current, sizePct: halfSize };
    newGroups.splice(idx + 1, 0, { id: newGroupId, sizePct: halfSize });
    return newGroups;
  }

  /** Remove a group and redistribute its space */
  removeGroup(groupId: string): EditorGroupState[] {
    if (this.groups.length <= 1) return this.groups;
    const idx = this.groups.findIndex((g) => g.id === groupId);
    if (idx < 0) return this.groups;

    const removed = this.groups[idx];
    const newGroups = this.groups.filter((g) => g.id !== groupId);

    // Give removed space to the adjacent group
    const neighborIdx = idx > 0 ? idx - 1 : 0;
    newGroups[neighborIdx] = {
      ...newGroups[neighborIdx],
      sizePct: newGroups[neighborIdx].sizePct + removed.sizePct,
    };
    return newGroups;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'sysml-editor-zone': SysmlEditorZone;
  }
}
